import { type SetNonNullable } from "type-fest";

export type SetOptions = {
  merge?: boolean;
  // mergeFields?: string[]; // TODO: support this if needed
};

export type DocumentChangeType = "added" | "modified" | "removed";

export type DocumentId = string & { __brand: "DocumentId" };

export type CollectionPath = string & { __brand: "CollectionPath" };

export type DocumentPath = string & { __brand: "DocumentPath" };

export type CollectionName = string & { __brand: "CollectionName" };

export function convertDocumentId(id: string): DocumentId {
  return id as DocumentId; // TODO validate doc id
}

export function convertCollectionName(id: string): CollectionName {
  return id as CollectionName; // TODO validate collection name
}

export function convertCollectionPath(id: string): CollectionPath {
  return id as CollectionPath; // TODO validate doc id
}

export function convertDocumentPath(path: string): DocumentPath {
  verifyDocumentPath(path.split("/"));

  return path as DocumentPath;
}

export function buildDocumentPath(collectionPath: CollectionPath, docId: DocumentId): DocumentPath {
  return `${collectionPath}/${docId}` as DocumentPath;
}

export function buildCollectionPath(
  parentPath: DocumentPath | undefined | null,
  collectionName: CollectionName
): CollectionPath {
  if (parentPath) {
    return `${parentPath}/${collectionName}` as CollectionPath;
  }

  return collectionName as string as CollectionPath;
}

export const verifyDocumentPath = (documentPathParts: string[]) => {
  if (documentPathParts.length % 2 !== 0) {
    throw new Error(`Invalid document path: ${documentPathParts.join("/")}`);
  }
};

type ParsedPath = {
  collectionName: CollectionName;
  parentPath: DocumentPath | undefined;
  documentId: DocumentId | undefined;
  fullDocumentId: DocumentId | undefined;
};

export type ParsedDocumentPath = SetNonNullable<ParsedPath, "documentId" | "fullDocumentId">;
export type ParsedCollectionPath = Pick<ParsedPath, "collectionName" | "parentPath">;

export function parseCollectionPath(path: string): ParsedCollectionPath {
  const parts = path.split("/").filter(Boolean); // remove empty parts

  const isOddLength = parts.length % 2 === 1;

  if (parts.length < 1 || !isOddLength) {
    throw new Error("Invalid collection path");
  }

  const collectionName = parts.at(-1) as string;
  const parentParts = parts.slice(0, -1);
  const parentPath = parentParts.length > 0 ? parentParts.join("/") : null;

  return {
    collectionName: collectionName as CollectionName,
    parentPath: parentPath as DocumentPath | undefined,
  };
}

export function parseDocumentPath(path: string): ParsedDocumentPath {
  const parts = path.split("/").filter(Boolean); // remove empty parts

  const isEvenLength = parts.length % 2 === 0;

  if (parts.length < 1 || !isEvenLength) {
    throw new Error("Invalid document path");
  }

  const collectionName = parts.at(-2) as string;
  const parentParts = parts.slice(0, -2);
  const parentPath = parentParts.length > 0 ? parentParts.join("/") : null;
  const documentId = parts.at(-1) as string;
  const fullDocumentId = [parentPath, documentId].filter(Boolean).join("/");

  return {
    collectionName: collectionName as CollectionName,
    documentId: documentId as DocumentId,
    fullDocumentId: fullDocumentId as DocumentId,
    parentPath: parentPath as DocumentPath | undefined,
  };
}

export type MongoNativeReference = {
  $ref: string;
  $id: string;
};
